// frontend/src/components/dashboard/pos/PosScreen.jsx
// cSpell:ignore bgcolor
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Alert, 
  Typography, 
  CircularProgress, 
  Button, 
  Stack 
} from '@mui/material';
import { 
  WifiOff, 
  ShoppingCart, 
  Error as ErrorIcon 
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import DashboardContext from '../contexts/DashboardContext';

// Import components
import TopAppBar from './components/TopAppBar';
import CategoriesSidebar from './components/CategoriesSidebar';
import ProductsSection from './components/ProductsSection';
import CartSection from './components/CartSection';
import MobileCartDrawer from './components/MobileCartDrawer';

import { usePOSSettings } from '../pages/posconfig/hooks/usePOSSettings';


// Import dialogs
import PaymentDialog from './dialogs/PaymentDialog';
import ChangeDialog from './dialogs/ChangeDialog';
import SuccessDialog from './dialogs/SuccessDialog';
import CalculatorDialog from './dialogs/CalculatorDialog';

// Import services
import menuService from '../services/menuService';

// Import hooks and styles
import useResponsiveLayout from './hooks/useResponsiveLayout';
import usePosData from './hooks/usePosData';
import usePosCart from './hooks/usePosCart';
import { commonStyles } from './styles/posStyles';
import './styles/pos-system.css';


const MotionDiv = motion.div;

const PosScreen = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { vendorInfo } = useContext(DashboardContext);
  
  // Enhanced responsive layout hook
  const { 
    deviceInfo,
    layoutConfig,
    shouldShowMobileCart,
    shouldShowDesktopCart,
    isTablet,
    hasSafeArea,
    getCartButtonSize
  } = useResponsiveLayout();
  
  // Custom hooks for data and cart management
  const { 
    categories, 
    loading, 
    error, 
    isOnline,
    filteredProducts,
    selectedCategory,
    searchTerm,
    setSelectedCategory,
    setSearchTerm
  } = usePosData();
  
  const {
    cartItems,
    addToCart,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    clearCart,
    calculateTotal
  } = usePosCart();

    // UI States
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [includeServiceCharge, setIncludeServiceCharge] = useState(false);
  const [includeGST, setIncludeGST] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [calculatorDialogOpen, setCalculatorDialogOpen] = useState(false);
  const [amountReceived, setAmountReceived] = useState('0');
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [changeAmount, setChangeAmount] = useState(0);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [deviceNumber, setDeviceNumber] = useState('');
  // const { settings } = usePOSSettings();

  const { settings, loading: settingsLoading, error: settingsError } = usePOSSettings();

  // Mobile optimization: Set viewport meta tag
  useEffect(() => {
    // Ensure proper viewport scaling on mobile
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }
    
    if (shouldShowMobileCart) {
      // Mobile-optimized viewport
      viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
      
      // Prevent zoom on input focus (iOS)
      const style = document.createElement('style');
      style.innerHTML = `
        @media screen and (max-width: 430px) {
          input, select, textarea {
            font-size: 16px !important;
          }
        }
      `;
      document.head.appendChild(style);
      
      return () => {
        document.head.removeChild(style);
      };
    } else {
      // Desktop/tablet viewport
      viewportMeta.content = 'width=device-width, initial-scale=1.0';
    }
  }, [shouldShowMobileCart]);

  useEffect(() => {
    // Settings will automatically update via usePOSSettings hook
    console.log('🔄 POS Settings updated in real-time:', {
      metadata: settings?.cartMetadata,
      enabledFields: Object.entries(settings?.cartMetadata || {})
        .filter(([, config]) => config.enabled && config.visible !== false)
        .map(([name, config]) => ({ name, label: config.label }))
    });
  }, [settings?.cartMetadata]);

  
  // ✅ ADD: Loading state - Add this right after the hooks
  if (settingsLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        bgcolor: '#f8fafc', // Light background instead of blue
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={48} sx={{ color: '#667eea' }} />
        <Typography variant="h6" color="text.secondary">
          Loading POS System...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7 }}>
          Please wait while we load your settings
        </Typography>
      </Box>
    );
  }

  // ✅ ADD: Error state - Add this right after the loading state
  if (settingsError) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        flexDirection: 'column',
        gap: 2,
        p: 3
      }}>
        <Box sx={{ 
          textAlign: 'center',
          maxWidth: 400,
          p: 3,
          borderRadius: 2,
          bgcolor: 'white',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
        }}>
          <ErrorIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
          <Typography variant="h6" color="error" sx={{ mb: 1 }}>
            Error Loading POS System
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {settingsError}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button 
              variant="contained" 
              onClick={() => window.location.reload()}
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
              }}
            >
              Retry
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  // ✅ ADD: Settings validation - Add this after error state
  if (!settings) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        bgcolor: '#f8fafc'
      }}>
        <Typography variant="h6" color="text.secondary">
          No POS settings found. Please configure your POS system.
        </Typography>
      </Box>
    );
  }



  // Handle device number change
  const handleDeviceNumberChange = (event) => {
    const value = event.target.value;
    const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '').substring(0, 10);
    setDeviceNumber(sanitizedValue);
  };

  // Payment handlers
  const handlePayment = () => {
    if (!isOnline) {
      alert(t('pos.error.requiresOnline'));
      return;
    }
    
    if (cartItems.length === 0) {
      alert(t('pos.error.emptyCart'));
      return;
    }
    
    setPaymentDialogOpen(true);
  };

  const processOrder = async (paymentMethod) => {
    console.log('💳 Processing order with payment method:', paymentMethod);
    
    // Don't process order if it's calculate_change - that should open calculator
    if (paymentMethod === 'calculate_change' || paymentMethod === 'calculateChange') {
      console.log('🧮 Redirecting to calculator instead of processing order');
      handleCalculatorToggle();
      return;
    }
    
    try {
      if (!isOnline) {
        alert(t('pos.error.requiresOnline'));
        return;
      }

      setSelectedPaymentMethod(paymentMethod);

      if (cartItems.length === 0) {
        alert(t('pos.error.emptyCart'));
        return;
      }
      
      // // Process direct payment (cash, card, etc.)
      // const orderData = {
      //   total_amount: calculateTotal(includeServiceCharge, includeGST).total,
      //   discount: 0,
      //   status: 'completed',
      //   note: '', 
      //   device_number: deviceNumber.trim(),
      //   items: cartItems.map(item => ({
      //     item_id: item.id,
      //     qty: item.quantity,
      //     price: item.price,
      //     discount: 0
      //   })),
      //   payment: {
      //     amount: calculateTotal(includeServiceCharge, includeGST).total,
      //     method: paymentMethod
      //   },
      //   created_at: new Date().toISOString()
      // };
      
      // const orderPromise = Promise.race([
      //   menuService.submitOrder(orderData),
      //   new Promise((_, reject) => 
      //     setTimeout(() => reject(new Error('Order timeout')), 15000)
      //   )
      // ]);
      
      // const response = await orderPromise;
      // console.log('Order processed:', response);
      
      const orderData = {
      total_amount: calculateTotal(includeServiceCharge, includeGST).total,
      discount: 0,
      status: 'completed',
      note: '', 
      device_number: deviceNumber.trim(),
      items: cartItems.map(item => ({
        item_id: item.id,
        qty: item.quantity,
        price: item.price,
        discount: 0
      })),
      payment: {
        amount: calculateTotal(includeServiceCharge, includeGST).total,
        method: paymentMethod
      },
      created_at: new Date().toISOString()
    };
    

    console.log('🔍 Submitting order with data:', orderData);

    // ✅ CHANGED: Use the new stock-aware function
    const response = await menuService.submitOrderWithStockReduction(orderData);
    console.log('Order processed with stock reduction:', response);


      setOpenSuccessDialog(true);
      
      setTimeout(() => {
        setOpenSuccessDialog(false);
        clearCart();
        setPaymentDialogOpen(false);
        setSelectedPaymentMethod(null);
        setIncludeServiceCharge(false);
        setIncludeGST(false);
        setDeviceNumber('');
      }, 2000);
    } catch (error) {
      console.error('Error processing order:', error);
      if (error.message === 'Order timeout') {
        alert(t('pos.error.orderTimeout'));
      } else {
        alert(t('pos.error.orderFailed'));
      }
    }
  };

  // Cash with change payment
  const handleCashWithChangePayment = () => {
    const totalAmount = calculateTotal(includeServiceCharge, includeGST).total;
    const received = parseFloat(amountReceived);
    
    if (received < totalAmount) {
      alert(t('pos.error.insufficientAmount'));
      return;
    }
    
    setSelectedPaymentMethod('cash');
    setChangeAmount(received - totalAmount);
    setCalculatorDialogOpen(false);
    setShowChangeDialog(true);
    
    const orderData = {
      total_amount: totalAmount,
      discount: 0,
      status: 'completed',
      note: '',
      device_number: deviceNumber.trim(),
      items: cartItems.map(item => ({
        item_id: item.id,
        qty: item.quantity,
        price: item.price,
        discount: 0
      })),
      payment: {
        amount: received,
        method: 'cash'
      },
      created_at: new Date().toISOString()
    };
    
    menuService.submitOrder(orderData)
      .then(response => {
        console.log('Order processed with change:', response);
      })
      .catch(error => {
        console.error('Error processing order with change:', error);
        alert(t('pos.error.orderFailed'));
      });
  };

  // Calculator handlers
  const handleCalculatorToggle = () => {
    console.log('🧮 Calculator toggle requested');
    console.log('📊 Current state:', { isOnline, cartItemsCount: cartItems.length, calculatorDialogOpen });
    
    if (!isOnline) {
      alert(t('pos.error.requiresOnline'));
      return;
    }
    
    if (cartItems.length === 0) {
      alert(t('pos.error.emptyCart'));
      return;
    }
    
    console.log('✅ Opening calculator dialog');
    setCalculatorDialogOpen(!calculatorDialogOpen);
    setAmountReceived('0');
  };

  const handleQuickAmount = (amount) => {
    const currentAmount = parseFloat(amountReceived === '0' ? '0' : amountReceived);
    const newAmount = (currentAmount + amount).toString();
    setAmountReceived(newAmount);
  };

  // Navigation
  const handleBackToDashboard = () => {
    if (cartItems.length > 0) {
      const confirmLeave = window.confirm(t('pos.confirmLeave'));
      if (!confirmLeave) return;
    }
    navigate('/dashboard');
  };

  // Mobile cart button click handler
  const handleMobileCartClick = () => {
    if (cartItems.length === 0) {
      // Optional: Show message or just open empty cart
      // alert(t('pos.cartEmpty'));
    }
    setIsDrawerOpen(true);
  };

  // Get cart button size based on device
  const cartButtonSize = getCartButtonSize();

  return (
    <Box 
      className="pos-system-container" 
      sx={{
        ...commonStyles.fixedHeightContainer,
        // Mobile-specific styles
        ...(shouldShowMobileCart && {
          paddingTop: hasSafeArea ? 'env(safe-area-inset-top)' : 0,
          paddingBottom: hasSafeArea ? 'env(safe-area-inset-bottom)' : 0,
          paddingLeft: hasSafeArea ? 'env(safe-area-inset-left)' : 0,
          paddingRight: hasSafeArea ? 'env(safe-area-inset-right)' : 0,
        })
      }}
      data-mobile={shouldShowMobileCart ? 'true' : 'false'}
    >
      {/* Network Status Alert */}
      {!isOnline && (
        <Alert 
          severity="warning" 
          icon={<WifiOff />}
          sx={{
            position: 'absolute',
            top: hasSafeArea ? 'env(safe-area-inset-top)' : 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            borderRadius: 0,
            justifyContent: 'center',
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            margin: 0
          }}
        >
          {t('pos.error.offline')} - {t('pos.error.offlineMode')}
        </Alert>
      )}

      {/* Top AppBar */}
      <TopAppBar
        vendorInfo={vendorInfo}
        cartItems={cartItems}
        calculateTotal={() => calculateTotal(includeServiceCharge, includeGST)}
        isOnline={isOnline}
        onBackToDashboard={handleBackToDashboard}
        onPayment={handlePayment}
        isMobile={shouldShowMobileCart}
        deviceInfo={deviceInfo}
      />
      
      {/* Main Content */}
      <Box sx={commonStyles.mainContentContainer}>
        {/* Categories Sidebar - Desktop/Tablet only */}
        {shouldShowDesktopCart && (
          <CategoriesSidebar
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            loading={loading}
            isMobile={shouldShowMobileCart}
            isTablet={isTablet}
            deviceInfo={deviceInfo}
            layoutConfig={layoutConfig}
          />
        )}
        
        {/* Products Section */}
       <ProductsSection
        categories={categories}
        products={filteredProducts}
        selectedCategory={selectedCategory}
        searchTerm={searchTerm}
        onCategoryChange={setSelectedCategory}
        onSearchChange={setSearchTerm}
        onAddToCart={addToCart}
        loading={loading}
        error={error}
        isOnline={isOnline}
        isMobile={shouldShowMobileCart}
        isTablet={isTablet}
        deviceInfo={deviceInfo}
        layoutConfig={layoutConfig}
        settings={settings} // Add this line
      />
        
        {/* Cart Section - Desktop/Tablet only */}
        {shouldShowDesktopCart && (
          <CartSection
            cartItems={cartItems}
            onIncreaseQuantity={increaseQuantity}
            onDecreaseQuantity={decreaseQuantity}
            onRemoveFromCart={removeFromCart}
            onClearCart={clearCart}
            calculateTotal={() => calculateTotal(includeServiceCharge, includeGST)}
            includeServiceCharge={includeServiceCharge}
            includeGST={includeGST}
            setIncludeServiceCharge={setIncludeServiceCharge}
            setIncludeGST={setIncludeGST}
            deviceNumber={deviceNumber}
            onDeviceNumberChange={handleDeviceNumberChange}
            onProcessOrder={processOrder}
            onCalculatorToggle={handleCalculatorToggle}
            selectedPaymentMethod={selectedPaymentMethod}
            calculatorDialogOpen={calculatorDialogOpen}
            isOnline={isOnline}
            isMobile={shouldShowMobileCart}
            isTablet={isTablet}
            deviceInfo={deviceInfo}
            layoutConfig={layoutConfig}
          />
        )}
      </Box>
      
      {/* ENHANCED Mobile Cart Button - iPhone optimized */}
      {shouldShowMobileCart && (
        <Box 
          className="mobile-cart-button-container"
          sx={{ 
            position: 'fixed !important', 
            bottom: hasSafeArea 
              ? `calc(env(safe-area-inset-bottom) + 20px)` 
              : { xs: 20, sm: 24 },
            left: '50% !important',
            transform: 'translateX(-50%) !important',
            zIndex: 1000,
            display: 'flex !important',
            opacity: '1 !important',
            visibility: 'visible !important',
            pointerEvents: 'auto !important'
          }}
        >
          <MotionDiv
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              transformOrigin: 'center bottom'
            }}
          >
            <Box
              className="mobile-cart-button" 
              onClick={handleMobileCartClick}
              sx={{
                display: 'flex !important',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                cursor: 'pointer',
                opacity: '1 !important',
                visibility: 'visible !important',
                pointerEvents: 'auto !important',
                
                // Device-specific sizing
                minWidth: cartButtonSize ? `${cartButtonSize.width}px` : { xs: 180, sm: 200 },
                minHeight: cartButtonSize ? `${cartButtonSize.height}px` : { xs: 52, sm: 56 },
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1.25, sm: 1.5 },
                
                // Enhanced styling
                borderRadius: cartButtonSize ? `${cartButtonSize.height / 2}px` : { xs: 26, sm: 28 },
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                
                // Fixed positioning
                position: 'fixed',
                bottom: hasSafeArea 
                  ? `calc(env(safe-area-inset-bottom) + 20px)` 
                  : { xs: 20, sm: 24 },
                left: '50%',
                transform: 'translateX(-50%)',
                
                '&:hover': {
                  transform: 'translateX(-50%) translateY(-3px)',
                  boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)'
                },
                '&:active': {
                  transform: 'translateX(-50%) translateY(-1px)'
                },
                
                // Enhanced touch area for mobile
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-10px',
                  left: '-10px',
                  right: '-10px',
                  bottom: '-10px',
                  borderRadius: 'inherit'
                }
              }}
            >
              <ShoppingCart sx={{ 
                fontSize: { xs: 22, sm: 24 },
                flexShrink: 0
              }} />
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'flex-start',
                minWidth: 0
              }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    lineHeight: 1,
                    opacity: 0.9,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {cartItems.length} {t('pos.items')}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                    lineHeight: 1,
                    whiteSpace: 'nowrap'
                  }}
                >
                  ${calculateTotal(includeServiceCharge, includeGST).total.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </MotionDiv>
        </Box>
      )}
        
      {/* Enhanced Mobile Cart Drawer - Full screen for iPhones */}
      <MobileCartDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        cartItems={cartItems}
        onIncreaseQuantity={increaseQuantity}
        onDecreaseQuantity={decreaseQuantity}
        onRemoveFromCart={removeFromCart}
        onClearCart={clearCart}
        calculateTotal={() => calculateTotal(includeServiceCharge, includeGST)}
        includeServiceCharge={includeServiceCharge}
        includeGST={includeGST}
        setIncludeServiceCharge={setIncludeServiceCharge}
        setIncludeGST={setIncludeGST}
        deviceNumber={deviceNumber}
        onDeviceNumberChange={handleDeviceNumberChange}
        onPayment={handlePayment}
        isOnline={isOnline}
        fullScreen={shouldShowMobileCart}
        hasSafeArea={hasSafeArea}
      />
      
      {/* Dialogs */}
      <PaymentDialog 
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        totalAmount={calculateTotal(includeServiceCharge, includeGST).total}
        selectedPaymentMethod={selectedPaymentMethod}
        processOrder={processOrder}
      />
      
      <ChangeDialog
        open={showChangeDialog}
        onClose={() => {
          setShowChangeDialog(false);
          clearCart();
          setAmountReceived('0');
          setCalculatorDialogOpen(false);
          setSelectedPaymentMethod(null);
          setIncludeServiceCharge(false);
          setIncludeGST(false);
        }}
        totalAmount={calculateTotal(includeServiceCharge, includeGST).total}
        amountReceived={amountReceived}
        changeAmount={changeAmount}
      />
      
      <SuccessDialog
        open={openSuccessDialog}
        totalAmount={calculateTotal(includeServiceCharge, includeGST).total}
        paymentMethod={selectedPaymentMethod}
        deviceNumber={deviceNumber}
      />
      
      <CalculatorDialog
        open={calculatorDialogOpen}
        onClose={() => setCalculatorDialogOpen(false)}
        amountReceived={amountReceived}
        setAmountReceived={setAmountReceived}
        handleQuickAmount={handleQuickAmount}
        handleCashWithChangePayment={handleCashWithChangePayment}
        cartItems={cartItems}
        totalAmount={calculateTotal(includeServiceCharge, includeGST).total}
      />
    </Box>
  );
};

export default PosScreen;